/**
 * Referring-sites rollup: inbound referrals grouped by sending facility,
 * with YoY, last-referral age, top sender, and optional activity columns.
 */

import { getActivityCount } from '../../utils/chartBinning';
import { DIRECTION_INBOUND, rowMatchesFacilityScope } from '../../utils/referralDirection';
import { pctChange } from './overviewCompute';

function f(record) {
  return record?.data || record?.Data || {};
}

function recordDay(record) {
  const data = f(record);
  const ts = record?.timestamp || data?.created_date;
  if (!ts) return null;
  if (typeof ts === 'object' && ts.value) return String(ts.value).slice(0, 10);
  return String(ts).slice(0, 10);
}

function facKey(id) {
  if (id == null || id === '') return '';
  const n = Number(id);
  if (Number.isFinite(n)) return String(Math.round(n));
  return String(id).trim();
}

function filterInbound(records, hubFacIds) {
  if (!Array.isArray(records)) return [];
  if (!hubFacIds?.size) return records;
  return records.filter(r => rowMatchesFacilityScope(f(r), 'referrals', hubFacIds, DIRECTION_INBOUND));
}

/** Activity at a referring facility (facility_id match). */
function sumActivityAtFacility(records, dataType, facilityId) {
  if (!facilityId || !Array.isArray(records)) return 0;
  let total = 0;
  for (const r of records) {
    const data = f(r);
    const fid = facKey(data.facility_id ?? data.receiving_facility_id);
    if (fid !== facilityId) continue;
    total += dataType === 'referrals' ? 1 : getActivityCount(r, dataType);
  }
  return total;
}

export function buildReferringSites({
  referralsCurrent,
  referralsPrior,
  loginsCurrent,
  messagesCurrent,
  studiesCurrent,
  hubFacIds,
  asOfDay,
}) {
  const hub = new Set([...(hubFacIds || [])].map(facKey).filter(Boolean));
  const current = filterInbound(referralsCurrent, hubFacIds);
  const prior = filterInbound(referralsPrior, hubFacIds);

  const sites = new Map();

  const bump = (map, data, day, isCurrent) => {
    const sid = facKey(data.sending_facility_id);
    if (!sid) return;
    // Skip when sender is itself a hub facility (internal spoke).
    if (hub.size && hub.has(sid)) return;

    let s = map.get(sid);
    if (!s) {
      s = {
        id: sid,
        name: data.sending_facility || `Facility ${sid}`,
        ref: 0,
        priorRef: 0,
        lastDay: null,
        senders: new Map(),
      };
      map.set(sid, s);
    }
    if (isCurrent) {
      s.ref += 1;
      if (day && (!s.lastDay || day > s.lastDay)) s.lastDay = day;
      const uid = data.sending_user_id != null ? String(data.sending_user_id) : '';
      const uname = data.sending_user_name || 'Unknown';
      if (uid || uname) {
        const key = uid || uname;
        const prev = s.senders.get(key) || { name: uname, role: data.sending_speciality || '', count: 0 };
        prev.count += 1;
        if (uname && uname !== 'Unknown') prev.name = uname;
        s.senders.set(key, prev);
      }
    } else {
      s.priorRef += 1;
    }
  };

  for (const r of current) bump(sites, f(r), recordDay(r), true);
  for (const r of prior) {
    const data = f(r);
    const sid = facKey(data.sending_facility_id);
    if (!sid || (hub.size && hub.has(sid))) continue;
    if (!sites.has(sid)) {
      sites.set(sid, {
        id: sid,
        name: data.sending_facility || `Facility ${sid}`,
        ref: 0,
        priorRef: 0,
        lastDay: null,
        senders: new Map(),
      });
    }
    sites.get(sid).priorRef += 1;
  }

  const asOf = asOfDay ? new Date(`${asOfDay}T00:00:00`) : new Date();

  return Array.from(sites.values()).map(s => {
    const delta = pctChange(s.ref, s.priorRef);
    let daysSince = null;
    if (s.lastDay) {
      daysSince = Math.floor((asOf - new Date(`${s.lastDay}T00:00:00`)) / 86400000);
    }
    let status = 'steady';
    let statusLabel = 'Steady';
    if (daysSince != null && daysSince >= 60) {
      status = 'quiet';
      statusLabel = `Quiet ${daysSince} days`;
    } else if (delta != null && delta < -10) {
      status = 'declining';
      statusLabel = 'Declining';
    } else if (delta != null && delta > 10) {
      status = 'growing';
      statusLabel = 'Growing';
    }

    const senderList = Array.from(s.senders.values()).sort((a, b) => b.count - a.count);
    const top = senderList[0];

    return {
      id: s.id,
      name: s.name,
      ref: s.ref,
      priorRef: s.priorRef,
      delta,
      lastDay: s.lastDay,
      daysSince,
      status,
      statusLabel,
      topSender: top?.name || '—',
      topSenderCount: top?.count || 0,
      senders: senderList.slice(0, 8),
      messages: sumActivityAtFacility(messagesCurrent, 'messages', s.id),
      logins: sumActivityAtFacility(loginsCurrent, 'logins', s.id),
      studies: sumActivityAtFacility(studiesCurrent, 'studies', s.id),
    };
  });
}

export function sortSites(rows, sortKey) {
  const copy = [...rows];
  if (sortKey === 'drop') copy.sort((a, b) => (a.delta ?? 0) - (b.delta ?? 0));
  else if (sortKey === 'quiet') copy.sort((a, b) => (b.daysSince ?? -1) - (a.daysSince ?? -1));
  else copy.sort((a, b) => b.ref - a.ref);
  return copy;
}
